import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import { fetchLocations } from "../services/locations";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    createOperatorPoll,
    fetchOperatorPolls,
    markOperatorPollsViewed,
    voteOperatorPoll,
} from "../services/polls";
import { isPastDateTimeLocal, toDateTimeLocalMinValue } from "../utils/dateInput";

function buildLocationLabel(loc) {
    if (!loc) return "";
    return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
}

function formatKyiv(dt) {
    if (!dt) return "";
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("uk-UA", {
        timeZone: "Europe/Kyiv",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(d).replace(",", "");
}

const OperatorPolls = ({ onPollsViewed }) => {
    const [items, setItems] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const [newQuestion, setNewQuestion] = useState("");
    const [newExpiresLocal, setNewExpiresLocal] = useState("");
    const [newLocationId, setNewLocationId] = useState("network");
    const [submitting, setSubmitting] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [expandedPollId, setExpandedPollId] = useState(null);
    const [sectionFilter, setSectionFilter] = useState("active");
    const [highlightedPollId, setHighlightedPollId] = useState(null);
    const pollRefs = useRef(new Map());

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [polls, locs] = await Promise.all([
                fetchOperatorPolls(),
                fetchLocations(),
            ]);
            setItems(Array.isArray(polls) ? polls : []);
            setLocations(Array.isArray(locs) ? locs : []);
        } catch (e) {
            setError(e?.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await markOperatorPollsViewed();
                if (!cancelled && typeof onPollsViewed === "function") onPollsViewed();
            } catch (_) {
                // ignore mark failures
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onVote = useCallback(async (pollId, answerYes) => {
        setError(null);
        try {
            await voteOperatorPoll(pollId, answerYes);
            await load();
        } catch (e) {
            setError(e?.message || "Не вдалося проголосувати");
        }
    }, [load]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const onChanged = () => load();
        window.addEventListener("wr_polls_changed", onChanged);
        return () => window.removeEventListener("wr_polls_changed", onChanged);
    }, [load]);

    useEffect(() => {
        if (!Array.isArray(items) || items.length === 0) return;

        let raw = null;
        try {
            raw = localStorage.getItem("pendingPollOpenId_operator");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const pollId = parseInt(raw, 10);
        if (!Number.isFinite(pollId) || pollId <= 0) {
            try {
                localStorage.removeItem("pendingPollOpenId_operator");
            } catch (_) {
                // ignore
            }
            return;
        }

        const item = items.find((poll) => Number(poll?.id) === pollId);
        if (!item) return;

        try {
            localStorage.removeItem("pendingPollOpenId_operator");
        } catch (_) {
            // ignore
        }

        setFilterFrom("");
        setFilterTo("");
        const expiresAt = new Date(item?.expires_at).getTime();
        setSectionFilter(Number.isFinite(expiresAt) && expiresAt < Date.now() ? "history" : "active");
        setExpandedPollId(pollId);
        setHighlightedPollId(pollId);

        const scrollToPoll = () => {
            const element = pollRefs.current.get(pollId);
            element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        };

        window.requestAnimationFrame(scrollToPoll);

        const timer = window.setTimeout(() => {
            setHighlightedPollId((prev) => (prev === pollId ? null : prev));
        }, 60000);

        return () => window.clearTimeout(timer);
    }, [items]);

    const onCreate = async () => {
        const question = (newQuestion || "").trim();
        if (question.length < 3) {
            setError("Питання занадто коротке");
            return;
        }

        if (!newExpiresLocal) {
            setError("Вкажіть кінцеву дату");
            return;
        }

        if (isPastDateTimeLocal(newExpiresLocal)) {
            setError("Дата «Активне до» не може бути в минулому");
            return;
        }

        let expiresIso = null;
        try {
            const d = new Date(newExpiresLocal);
            if (Number.isNaN(d.getTime())) throw new Error();
            expiresIso = d.toISOString();
        } catch (_) {
            setError("Некоректна кінцева дата");
            return;
        }

        const location_id = newLocationId === "network" ? null : parseInt(newLocationId);
        if (location_id !== null && (!Number.isFinite(location_id) || location_id <= 0)) {
            setError("Некоректна локація");
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await createOperatorPoll({ question, expires_at: expiresIso, location_id });
            setNewQuestion("");
            setNewExpiresLocal("");
            setNewLocationId("network");
            setShowCreateModal(false);
            await load();
        } catch (e) {
            setError(e?.message || "Не вдалося створити опитування");
        } finally {
            setSubmitting(false);
        }
    };

    const locationOptions = useMemo(() => {
        const base = [{ id: "network", label: "Вся мережа" }];
        const locs = Array.isArray(locations) ? locations : [];
        locs
            .filter((l) => l && l.status !== false)
            .sort((a, b) => buildLocationLabel(a).localeCompare(buildLocationLabel(b), "uk"))
            .forEach((l) => base.push({ id: String(l.id), label: buildLocationLabel(l) || (l.location_name || "") }));
        return base;
    }, [locations]);

    const filteredItems = useMemo(() => {
        const fromMs = filterFrom ? new Date(`${filterFrom}T00:00:00`).getTime() : null;
        const toMs = filterTo ? new Date(`${filterTo}T23:59:59.999`).getTime() : null;
        const hasFrom = Number.isFinite(fromMs);
        const hasTo = Number.isFinite(toMs);
        const query = searchQuery.trim().toLowerCase();

        return (items || []).filter((p) => {
            if (query) {
                const question = String(p?.question || "").toLowerCase();
                if (!question.includes(query)) return false;
            }

            if (!hasFrom && !hasTo) return true;
            if (!p || !p.created_at) return true;
            const ms = new Date(p.created_at).getTime();
            if (!Number.isFinite(ms)) return true;
            if (hasFrom && ms < fromMs) return false;
            if (hasTo && ms > toMs) return false;
            return true;
        });
    }, [items, filterFrom, filterTo, searchQuery]);

    const activePolls = useMemo(() => {
        const now = Date.now();
        return (filteredItems || []).filter((poll) => {
            const expires = new Date(poll?.expires_at).getTime();
            return !Number.isFinite(expires) || expires >= now;
        });
    }, [filteredItems]);

    const archivedPolls = useMemo(() => {
        const now = Date.now();
        return (filteredItems || []).filter((poll) => {
            const expires = new Date(poll?.expires_at).getTime();
            return Number.isFinite(expires) && expires < now;
        });
    }, [filteredItems]);

    const visibleActivePolls = sectionFilter === "history" ? [] : activePolls;
    const visibleArchivedPolls = sectionFilter === "active" ? [] : archivedPolls;

    const getPollPreview = (text) => {
        const raw = String(text || "").replace(/\s+/g, " ").trim();
        if (!raw) return "Без тексту";
        return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
    };

    const filterActive = Boolean(sectionFilter !== "active" || filterFrom || filterTo);

    return (
        <div className="wr-polls-page">
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn toolbar-icon-btn"
                        type="button"
                        onClick={() => {
                            setError(null);
                            setShowCreateModal(true);
                        }}
                        title="Створити опитування"
                        aria-label="Створити опитування"
                    >
                        <Plus size={16} />
                    </button>
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        type="button"
                        onClick={load}
                        disabled={loading}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={filterActive}
                        title="Фільтри опитувань"
                        hint="За замовчуванням показано активні. Період — за датою створення."
                        buttonTitle="Фільтри опитувань"
                        onClear={() => {
                            setSectionFilter("active");
                            setFilterFrom("");
                            setFilterTo("");
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Розділ</span>
                            <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
                                <option value="active">Активні</option>
                                <option value="history">Історія</option>
                                <option value="all">Усі</option>
                            </select>
                        </label>
                        <DateRangeFields
                            from={filterFrom}
                            to={filterTo}
                            onFromChange={setFilterFrom}
                            onToChange={setFilterTo}
                        />
                    </FilterPopover>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за питанням"
                        title="Пошук опитувань"
                    />
                </div>
            </div>

            {showCreateModal ? (
                <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Створити опитування</h3>
                        <textarea
                            rows={3}
                            className="wr-polls-create-textarea"
                            placeholder="Сформулюй коротке запитання для мешканців або правління..."
                            value={newQuestion}
                            onChange={(e) => setNewQuestion(e.target.value)}
                        />
                        <div className="wr-polls-create-inline-row wr-polls-create-inline-row--compact">
                            <label className="wr-polls-create-field">
                                <span>Кому показати</span>
                                <select value={newLocationId} onChange={(e) => setNewLocationId(e.target.value)}>
                                    {locationOptions.map((o) => (
                                        <option key={o.id} value={o.id}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="wr-polls-create-field wr-polls-create-field--date">
                                <span>Активне до</span>
                                <input
                                    className="wr-date-input"
                                    type="datetime-local"
                                    value={newExpiresLocal}
                                    min={toDateTimeLocalMinValue()}
                                    onChange={(e) => setNewExpiresLocal(e.target.value)}
                                />
                            </label>
                        </div>
                        {error ? <div style={{ marginTop: 8 }}>{error}</div> : null}
                        <div className="modal-actions">
                            <button className="add-btn" onClick={onCreate} disabled={submitting}>
                                {submitting ? "Створення..." : "Створити"}
                            </button>
                            <button className="add-btn cancel-btn" type="button" onClick={() => setShowCreateModal(false)}>
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {!showCreateModal && error ? <div style={{ marginBottom: 8 }}>{error}</div> : null}

            {loading ? <div>Завантаження...</div> : null}
            {!loading && (!Array.isArray(items) || items.length === 0) ? <div>Опитувань поки немає</div> : null}

            {!loading && Array.isArray(items) && items.length > 0 && (!Array.isArray(filteredItems) || filteredItems.length === 0) ? (
                <div>Нічого не знайдено за вибраними фільтрами</div>
            ) : null}

            {!loading && Array.isArray(filteredItems) && filteredItems.length > 0
                && visibleActivePolls.length === 0 && visibleArchivedPolls.length === 0 ? (
                <div>Немає опитувань у вибраному розділі</div>
            ) : null}

            {(visibleActivePolls || []).length > 0 ? (
                <div className="list-card" style={{ marginBottom: 12 }}>
                    <h3 style={{ marginTop: 0 }}>Активні опитування</h3>
                    <div className="operator-accordion-list">
                        {visibleActivePolls.map((p) => {
                            const isExpanded = expandedPollId === p.id;
                            return (
                                <div
                                    key={p.id}
                                    ref={(el) => {
                                        if (el) pollRefs.current.set(p.id, el);
                                        else pollRefs.current.delete(p.id);
                                    }}
                                    className={`operator-accordion-item ${highlightedPollId === p.id ? "is-highlighted" : ""}`}
                                    style={{ background: highlightedPollId === p.id ? "#f8fbff" : undefined }}
                                >
                                    <button type="button" className="operator-accordion-head" onClick={() => setExpandedPollId((prev) => prev === p.id ? null : p.id)}>
                                        <div className="operator-accordion-head-main">
                                            <div className="operator-accordion-title">{p.question}</div>
                                            <div className="operator-accordion-meta">
                                                <span>{p.location_name ? p.location_name : "Вся мережа"}</span>
                                                <span>До {formatKyiv(p.expires_at)}</span>
                                                <span>Відповіли {Number(p?.responded_count || 0)}</span>
                                            </div>
                                            <div className="operator-accordion-preview">{getPollPreview(p.question)}</div>
                                        </div>
                                        <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                    </button>

                                    {isExpanded ? (
                                        <div className="operator-accordion-body">
                                            <div className="operator-detail-grid">
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Участь</span>
                                                    <div className="operator-detail-value">
                                                        Відповіли: {Number(p?.responded_count || 0)}
                                                        {(p?.total_clients_network || p?.total_operators_network)
                                                            ? ` / ${Number(p?.total_clients_network || 0) + Number(p?.total_operators_network || 0)}`
                                                            : ""}
                                                    </div>
                                                </div>
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Результат</span>
                                                    <div className="operator-detail-value">Так: {Number(p?.yes_count || 0)} · Ні: {Number(p?.no_count || 0)}</div>
                                                </div>
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">Розподіл</span>
                                                    <div className="operator-detail-value">
                                                        Клієнтів: {Number(p?.responded_clients_count || 0)}/{Number(p?.total_clients_network || 0)} · Правління: {Number(p?.responded_board_count || 0)}/{Number(p?.total_operators_network || 0)}
                                                    </div>
                                                </div>
                                                {p?.my_vote_yes === true ? <div className="operator-detail-note">Ваша відповідь: Так</div> : null}
                                                {p?.my_vote_yes === false ? <div className="operator-detail-note">Ваша відповідь: Ні</div> : null}
                                            </div>

                                            {p?.can_vote ? (
                                                <div className="config-actions" style={{ marginTop: 12 }}>
                                                    <button className="add-btn" onClick={() => onVote(p.id, true)}>Так</button>
                                                    <button className="add-btn refresh" onClick={() => onVote(p.id, false)}>Ні</button>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {(visibleArchivedPolls || []).length > 0 ? (
                <div className="list-card">
                    <h3 style={{ marginTop: 0 }}>Історія опитувань</h3>
                    <div className="operator-accordion-list">
                        {visibleArchivedPolls.map((p) => {
                            const isExpanded = expandedPollId === p.id;
                            return (
                                <div
                                    key={p.id}
                                    ref={(el) => {
                                        if (el) pollRefs.current.set(p.id, el);
                                        else pollRefs.current.delete(p.id);
                                    }}
                                    className={`operator-accordion-item ${highlightedPollId === p.id ? "is-highlighted" : ""}`}
                                    style={{ background: highlightedPollId === p.id ? "#f8fbff" : undefined }}
                                >
                                    <button type="button" className="operator-accordion-head" onClick={() => setExpandedPollId((prev) => prev === p.id ? null : p.id)}>
                                        <div className="operator-accordion-head-main">
                                            <div className="operator-accordion-title">{p.question}</div>
                                            <div className="operator-accordion-meta">
                                                <span>{p.location_name ? p.location_name : "Вся мережа"}</span>
                                                <span>Завершено {formatKyiv(p.expires_at)}</span>
                                                <span>Так {Number(p?.yes_count || 0)} · Ні {Number(p?.no_count || 0)}</span>
                                            </div>
                                        </div>
                                        <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                    </button>

                                    {isExpanded ? (
                                        <div className="operator-accordion-body">
                                            <div className="operator-detail-grid">
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Відповіли</span>
                                                    <div className="operator-detail-value">{Number(p?.responded_count || 0)}</div>
                                                </div>
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Усього в мережі</span>
                                                    <div className="operator-detail-value">{Number(p?.total_clients_network || 0) + Number(p?.total_operators_network || 0)}</div>
                                                </div>
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">Розподіл</span>
                                                    <div className="operator-detail-value">
                                                        Клієнтів: {Number(p?.responded_clients_count || 0)}/{Number(p?.total_clients_network || 0)} · Правління: {Number(p?.responded_board_count || 0)}/{Number(p?.total_operators_network || 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default OperatorPolls;
